import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700">返回首页</Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category_slug === article.category_slug && a.id !== article.id)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.category, href: `/category/${article.category_slug}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publish_date,
    image: article.cover_image,
    publisher: {
      '@type': 'Organization',
      name: '久久综合',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <SEOHead
        title={`${article.title} - 久久综合`}
        description={article.description}
        keywords={article.tags.join(',') + ',久久综合'}
        canonicalUrl={`https://jiujiuzonghe.com/article/${article.id}`}
        ogType="article"
      />
      <main className="min-h-screen pt-20 pb-12 md:pb-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <article>
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category_slug}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full"
                >
                  {article.category}
                </Link>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-500 text-xs rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.publish_date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i> {article.read_time}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读
                </span>
              </div>
            </header>

            <div className="w-full h-64 md:h-96 rounded-xl overflow-hidden mb-8">
              <img
                src={article.cover_image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                prose-h3:font-heading prose-h3:text-xl prose-h3:font-bold prose-h3:text-foreground-900 prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                [&_p]:text-foreground-700 [&_p]:leading-relaxed [&_p]:mb-4
                [&_h3]:font-heading [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-4
              "
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </article>

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <h3 className="font-heading text-lg font-bold text-foreground-900 mb-5">相关推荐</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.id}`}
                  className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="relative w-full h-36 overflow-hidden">
                    <img
                      src={related.cover_image}
                      alt={related.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3">
                    <h4 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {related.title}
                    </h4>
                    <p className="text-xs text-foreground-400 mt-1">{related.publish_date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}